"use client";

import { useEffect, useRef, useState } from "react";

/** Facebook's player for a portrait video (the Dastaan clips are 9:16). */
const PLAYER_WIDTH = 326;
const PLAYER_HEIGHT = 580;

/**
 * One Facebook video, in Facebook's own embedded player.
 *
 * Like the Instagram embed, nothing from Facebook loads until the card is
 * about to scroll into view — the player brings Facebook's scripts and
 * cookies, and most visitors never reach this far down the page. The link
 * has already been reduced to a canonical facebook.com video address
 * (lib/facebook.ts), so the player address is built from trusted parts.
 */
export function FacebookVideo({ url, title }: { url: string; title: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: "800px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const src =
    "https://www.facebook.com/plugins/video.php?" +
    new URLSearchParams({
      href: url,
      show_text: "false",
      width: String(PLAYER_WIDTH),
      height: String(PLAYER_HEIGHT),
    }).toString();

  return (
    <div ref={ref} className="relative aspect-[326/580] w-full overflow-hidden bg-black">
      {near ? (
        <iframe
          src={src}
          title={title}
          width={PLAYER_WIDTH}
          height={PLAYER_HEIGHT}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          className="absolute inset-0 h-full w-full border-0"
        />
      ) : null}
    </div>
  );
}
